import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { format, endOfMonth } from "date-fns";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getMetricData } from "../../api/metric";
import {
  getAdvancedMetricSummary,
  getAdvancedMetricGraphData,
  getCategoryBreakdown,
} from "../../api/expenseBreakdown";
import { getPlValues } from "../../api/growthPlan";
import { fetchJournalEntries } from "../../api/transactions";
import {
  getRangeAdvancedMetricSummary,
  getRangeAdvancedMetricTrend,
  getRangeCategoryBreakdown,
  getRangeMetricData,
  getRangePLValues,
} from "../../api/reportRange";

/* ─── helpers ─── */
const currency = (v) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v || 0);

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const normaliseAbs = (v) => Math.abs(toNumber(v));

const pct = (v) => `${Math.abs(toNumber(v)).toFixed(1)}%`;

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return format(new Date(dateStr + "T00:00:00"), "MMM yyyy");
  } catch {
    return dateStr;
  }
};

/* ─── design tokens ─── */
const FONT = "'Rethink Sans', Helvetica, sans-serif";
const NAVY = "#0B1F3A";
const PRIMARY = "#536DEC";
// Cap for trend chart X-axis so month labels stay fixed regardless of data scale
const TREND_CHART_X_MAX = 2_000_000;

const CATEGORY_COLORS = [PRIMARY, "#9CA3AF", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#06B6D4"];

/* ─── sub-components ─── */

const SectionTitle = ({ children }) => (
  <Box sx={{ position: "relative", pt: 2, pb: 1.5 }}>
    <Typography
      component="span"
      sx={{
        position: "absolute",
        left: 0,
        top: -20,
        transform: "translateY(-50%)",
        display: "inline-flex",
        alignItems: "center",
        bgcolor: NAVY,
        color: "#fff",
        borderRadius: "6px",
        px: 1.5,
        py: 0.5,
        fontFamily: FONT,
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const OverviewKpi = ({ label, value, subLabel, subColor }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      borderRight: "1px solid #E5E7EB",
      "&:last-child": { borderRight: "none" },
      px: 3,
      "&:first-of-type": { pl: 0 },
    }}
  >
    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "rgba(0,0,0,0.5)", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 500, color: subColor || "#6B7280", mt: 0.5 }}>
      {subLabel}
    </Typography>
  </Box>
);

const HorizontalBarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: "#fff", px: 1.5, py: 1, borderRadius: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: NAVY }}>
        {payload[0]?.payload?.month}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: PRIMARY }}>
        {currency(payload[0]?.value)}
      </Typography>
    </Box>
  );
};

const DonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: "#fff", px: 1.5, py: 1, borderRadius: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600 }}>
        {payload[0]?.name}: {currency(payload[0]?.value)}
      </Typography>
    </Box>
  );
};

/* ─── main report component ─── */

const TeamExpensesReport = forwardRef(function TeamExpensesReport(
  {
    customerId,
    startDate,
    endDate,
    expenseType = "team",
    reportTitle = "Team Expenses\nOverview Report",
  },
  ref
) {
  const endDateStr = useMemo(() => {
    if (!endDate) return format(endOfMonth(new Date()), "yyyy-MM-dd");
    return endDate;
  }, [endDate]);

  // startDateStr is only set when a custom range is explicitly provided.
  const startDateStr = useMemo(() => {
    if (!startDate) return null;
    try { return format(new Date(startDate + "T00:00:00"), "yyyy-MM-dd"); }
    catch { return null; }
  }, [startDate]);

  // When startDate is provided, use the range API endpoints.
  const useRangeApi = !!startDateStr;

  const isMultiMonth = useRangeApi && startDateStr.substring(0, 7) !== endDateStr.substring(0, 7);
  const periodLabel = isMultiMonth ? "previous period" : "last month";

  const reportLabel = useMemo(() => {
    if (isMultiMonth) {
      return `${formatDisplayDate(startDateStr)} – ${formatDisplayDate(endDateStr)}`;
    }
    return endDate ? formatDisplayDate(endDate) : format(new Date(), "MMM yyyy");
  }, [startDateStr, endDateStr, endDate, isMultiMonth]);

  // The Financial Trend section always shows the 12 months ending at endDateStr,
  // regardless of the selected report period.
  const trendStartDateStr = useMemo(() => {
    const base = endDateStr || format(new Date(), "yyyy-MM-dd");
    const end = new Date(base);
    const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    return format(start, "yyyy-MM-dd");
  }, [endDateStr]);

  const [loading, setLoading] = useState(false);
  const [teamSummary, setTeamSummary] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [metricData, setMetricData] = useState(null);
  const [plData, setPlData] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!customerId) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const sig = controller.signal;

    const load = async () => {
      setLoading(true);
      try {
        let summaryRes, trendRes, catRes, metricRes, plRes, txRes;

        if (useRangeApi) {
          // Use date-range endpoints for custom period selection.
          // Trend always covers the 12 months ending at endDateStr (trendStartDateStr),
          // regardless of the selected report window.
          [summaryRes, trendRes, catRes, metricRes, plRes, txRes] =
            await Promise.allSettled([
              getRangeAdvancedMetricSummary(customerId, expenseType, startDateStr, endDateStr, sig),
              getRangeAdvancedMetricTrend(customerId, expenseType, trendStartDateStr, endDateStr, sig),
              getRangeCategoryBreakdown(customerId, startDateStr, endDateStr, expenseType, sig),
              getRangeMetricData(customerId, startDateStr, endDateStr, sig),
              getRangePLValues(customerId, startDateStr, endDateStr, sig),
              fetchJournalEntries(customerId, { page_size: 10, ordering: "-amount", start_date: startDateStr, end_date: endDateStr }, sig),
            ]);
        } else {
          // Use existing single-date endpoints (legacy / current-month / YTD).
          // getAdvancedMetricGraphData already returns the 12-month window by default.
          [summaryRes, trendRes, catRes, metricRes, plRes, txRes] =
            await Promise.allSettled([
              getAdvancedMetricSummary(customerId, endDateStr, expenseType, sig, { adjusted: false, ytd: false }),
              getAdvancedMetricGraphData(customerId, endDateStr, expenseType, sig, { adjusted: false, ytd: false }),
              getCategoryBreakdown(customerId, endDateStr, expenseType, sig),
              getMetricData(customerId, endDateStr, sig, { adjusted: false, ytd: false }),
              getPlValues(customerId, endDateStr, sig, { adjusted: false }),
              fetchJournalEntries(customerId, { page_size: 10, ordering: "-amount" }, sig),
            ]);
        }

        if (summaryRes.status === "fulfilled")
          setTeamSummary(summaryRes.value?.data || null);
        if (trendRes.status === "fulfilled")
          setTrendData(trendRes.value?.data || null);
        if (catRes.status === "fulfilled") {
          const raw = catRes.value?.data;
          // Handle both array response and object with items/categories/results key
          const items = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.items)
            ? raw.items
            : Array.isArray(raw?.categories)
            ? raw.categories
            : Array.isArray(raw?.results)
            ? raw.results
            : [];
          setCategoryData(items);
        }
        if (metricRes.status === "fulfilled")
          setMetricData(metricRes.value?.data || null);
        if (plRes.status === "fulfilled")
          setPlData(plRes.value?.data || null);
        if (txRes.status === "fulfilled") {
          const raw = txRes.value?.data;
          const items = Array.isArray(raw) ? raw : raw?.results || [];
          setTransactions(items.slice(0, 5));
        }
      } catch (err) {
        if (err?.name !== "AbortError" && err?.name !== "CanceledError") {
          console.warn("Team expenses report load error", err);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [customerId, endDateStr, startDateStr, trendStartDateStr, useRangeApi, expenseType]);

  /* ── derived values ── */
  const totalTeamCost = normaliseAbs(
    teamSummary?.current_month_total ?? teamSummary?.actual ?? teamSummary?.pnl_value ?? 0
  );
  const teamChange = toNumber(
    teamSummary?.change_from_last_month_pct ?? teamSummary?.change_from_last_month ?? 0
  );
  const income = normaliseAbs(metricData?.income);
  const pctOfIncome = income > 0 ? ((totalTeamCost / income) * 100).toFixed(1) : "—";

  const plVariance = toNumber(plData?.variance ?? plData?.profit_variance ?? 0);
  const plIncome = normaliseAbs(metricData?.income ?? 0);
  const plIncomeTarget = toNumber(plData?.income_target ?? plData?.target_income ?? 0);
  const plIncomeVsTarget = plIncome - plIncomeTarget;

  // 12-month horizontal bar chart data
  const trendChartData = useMemo(() => {
    const months = trendData?.months ?? trendData?.labels ?? [];
    const values =
      trendData?.values ??
      trendData?.team ??
      trendData?.expenses ??
      trendData?.data ??
      [];
    if (!months.length) return [];
    return months.map((month, i) => ({
      month,
      value: normaliseAbs(values[i] ?? 0),
    }));
  }, [trendData]);

  // Category donut data
  const donutData = useMemo(() => {
    if (!categoryData.length) return [];
    return categoryData.map((c) => ({
      name: c.name ?? c.category ?? c.label ?? "Other",
      value: normaliseAbs(c.amount ?? c.value ?? c.total ?? 0),
      lastMonth: normaliseAbs(c.last_month ?? c.previous ?? 0),
      change: toNumber(c.change_pct ?? c.percent_change ?? 0),
    }));
  }, [categoryData]);

  const today = format(new Date(), "dd MMM yyyy, HH:mm");

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 400 }}>
        <CircularProgress size={32} sx={{ color: PRIMARY }} />
        <Typography sx={{ mt: 2, fontFamily: FONT, color: "text.secondary", fontSize: 14 }}>
          Loading report data…
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        bgcolor: "#F7F8FC",
        fontFamily: FONT,
        "& *": { fontFamily: `${FONT} !important`, boxSizing: "border-box" },
        "@media print": {
          bgcolor: "#F7F8FC",
          "& .no-print": { display: "none !important" },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          backgroundImage: "url('/Report_header.svg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: 140,
          aspectRatio: "595/140",
          p: { xs: "24px 28px", md: "32px 40px" },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ maxWidth: "50%", marginTop: "0.5em" }}>
          <Typography
            sx={{
              color: "#FFF",
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "var(--fontFamily, \"Rethink Sans\")",
              fontSize: "31px",
              fontStyle: "italic",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            Performance{"\n"}Overview Report
          </Typography>
        </Box>
        <Stack alignItems="flex-start" spacing={1.5} style={{ marginTop: "2em" }}>
          <Box
            component="img"
            src="/lucro-logo-white.png"
            alt="Lucro"
            sx={{ height: 28, width: "auto", objectFit: "contain" }}
          />
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: "#fff" }}>
            {reportLabel}
          </Typography>
        </Stack>
      </Box>

      {/* ── Content ── */}
      <Box
        sx={{
          p: { xs: "20px 16px", md: "28px 32px" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* ── Monthly Overview ── */}
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            p: "20px 24px",
            bgcolor: "#fff",
          }}
        >
          <SectionTitle>Monthly Overview</SectionTitle>
          <Stack direction={{ xs: "column", sm: "row" }} sx={{ mt: 1 }}>
            <OverviewKpi
              label="Total Team Cost"
              value={currency(totalTeamCost)}
              subLabel={`${pctOfIncome}% of income`}
              subColor={teamChange <= 0 ? "#2E7D32" : "#C62828"}
            />
            <OverviewKpi
              label="Perfect P&L Variance"
              value={currency(plVariance)}
              subLabel={
                plVariance === 0
                  ? "On target"
                  : plVariance < 0
                  ? `${pct(plVariance / (plIncome || 1) * 100)} under target`
                  : `${pct(plVariance / (plIncome || 1) * 100)} above target`
              }
              subColor={plVariance >= 0 ? "#2E7D32" : "#C62828"}
            />
            <OverviewKpi
              label="Perfect P&L Income"
              value={currency(plIncome)}
              subLabel={
                plIncomeVsTarget === 0
                  ? "On target"
                  : plIncomeVsTarget > 0
                  ? `${currency(plIncomeVsTarget)} above target`
                  : `${currency(Math.abs(plIncomeVsTarget))} below target`
              }
              subColor={plIncomeVsTarget >= 0 ? "#2E7D32" : "#C62828"}
            />
          </Stack>
        </Box>

        {/* ── Chart + Category + Transactions ── */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
          {/* Left: Financial Trend (horizontal bars) — always 12 months ending at endDateStr */}
          <Box
            sx={{
              flex: "1 1 50%",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              p: "20px 24px",
              bgcolor: "#fff",
              minWidth: 0,
            }}
          >
            <SectionTitle>12-Month Financial Trend</SectionTitle>
            {trendChartData.length > 0 ? (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={trendChartData}
                    layout="vertical"
                    margin={{ top: 0, right: 60, left: 0, bottom: 0 }}
                    barSize={12}
                  >
                    <XAxis
                      type="number"
                      hide
                      domain={[0, (dataMax) => Math.max(1, Math.min((dataMax || 0) * 1.15, TREND_CHART_X_MAX))]}
                    />
                    <YAxis
                      type="category"
                      dataKey="month"
                      tick={{ fontSize: 11, fontFamily: FONT, fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                      width={80}
                    />
                    <Tooltip content={<HorizontalBarTooltip />} cursor={{ fill: "rgba(83,109,236,0.06)" }} />
                    <Bar dataKey="value" fill={PRIMARY} radius={[0, 3, 3, 0]}>
                      {trendChartData.map((_, i) => (
                        <Cell key={i} fill={PRIMARY} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ height: 280 }}>
                <Typography sx={{ fontFamily: FONT, color: "text.secondary", fontSize: 13 }}>
                  No trend data available
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Right column: Category Breakdown + Top 5 Transactions */}
          <Stack sx={{ flex: "1 1 50%", minWidth: 0 }} spacing={2}>
            {/* Category Breakdown (donut) */}
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                p: "20px 24px",
                bgcolor: "#fff",
              }}
            >
              <SectionTitle>Category Breakdown</SectionTitle>
              {donutData.length > 0 ? (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ width: 120, height: 120, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={donutData}
                          cx="50%"
                          cy="50%"
                          innerRadius={36}
                          outerRadius={56}
                          dataKey="value"
                          strokeWidth={2}
                          stroke="#fff"
                        >
                          {donutData.map((_, i) => (
                            <Cell
                              key={i}
                              fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<DonutTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                    {donutData.map((item, i) => (
                      <Stack
                        key={item.name}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 11,
                              color: "rgba(0,0,0,0.65)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 140,
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: NAVY, flexShrink: 0 }}>
                          {currency(item.value)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              ) : (
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "text.secondary", py: 1 }}>
                  No category data available.
                </Typography>
              )}
            </Box>

            {/* Top 5 Transactions */}
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                p: "20px 24px",
                bgcolor: "#fff",
                flex: 1,
              }}
            >
              <SectionTitle>Top 5 Transactions</SectionTitle>
              {transactions.length === 0 ? (
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "text.secondary", py: 1 }}>
                  No transactions available.
                </Typography>
              ) : (
                <Stack spacing={0} divider={<Divider sx={{ borderColor: "#F3F4F6" }} />}>
                  {transactions.slice(0, 5).map((tx, i) => {
                    const amt = toNumber(tx.amount);
                    return (
                      <Stack
                        key={tx.id ?? i}
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ py: 1 }}
                      >
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            color: PRIMARY,
                            width: 20,
                            flexShrink: 0,
                          }}
                        >
                          #{i + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 11,
                            color: "rgba(0,0,0,0.45)",
                            flexShrink: 0,
                            width: 80,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tx.vendor_name || "Vendor Name"}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 11,
                            color: "rgba(0,0,0,0.65)",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tx.description || tx.name || tx.category || "—"}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 11,
                            fontWeight: 700,
                            color: NAVY,
                            flexShrink: 0,
                          }}
                        >
                          {currency(Math.abs(amt))}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>

        {/* ── Detailed Breakdown ── */}
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            p: "20px 24px",
            bgcolor: "#fff",
          }}
        >
          <SectionTitle>Detailed Breakdown</SectionTitle>
          {/* Table header */}
          <Stack
            direction="row"
            sx={{
              bgcolor: "#F8F9FC",
              borderRadius: "6px",
              px: 2,
              py: 1,
              mb: 0.5,
            }}
          >
            <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.5)", flex: 2 }}>
              Category
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.5)", flex: 1, textAlign: "right" }}>
              This Month
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.5)", flex: 1, textAlign: "right" }}>
              Last Month
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.5)", flex: 1, textAlign: "right" }}>
              % Change
            </Typography>
          </Stack>
          {donutData.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "text.secondary", py: 2 }}>
              No breakdown data available.
            </Typography>
          ) : (
            <Stack spacing={0} divider={<Divider sx={{ borderColor: "#F3F4F6" }} />}>
              {donutData.map((item, i) => {
                const changeColor =
                  item.change === 0
                    ? "#6B7280"
                    : item.change > 0
                    ? "#C62828"
                    : "#2E7D32";
                const changeLabel =
                  item.change === 0
                    ? "0.0%"
                    : `${item.change > 0 ? "+" : ""}${item.change.toFixed(1)}%`;
                return (
                  <Stack
                    key={item.name}
                    direction="row"
                    sx={{ px: 2, py: 1.25 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 2 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: NAVY }}>
                        {item.name}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: NAVY, flex: 1, textAlign: "right" }}>
                      {currency(item.value)}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "rgba(0,0,0,0.55)", flex: 1, textAlign: "right" }}>
                      {currency(item.lastMonth)}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: changeColor, flex: 1, textAlign: "right" }}>
                      {changeLabel}
                    </Typography>
                  </Stack>
                );
              })}
              {/* Total row */}
              <Stack
                direction="row"
                sx={{ px: 2, py: 1.25, bgcolor: "#F8F9FC", borderRadius: "0 0 6px 6px" }}
              >
                <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: NAVY, flex: 2 }}>
                  Total
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: NAVY, flex: 1, textAlign: "right" }}>
                  {currency(donutData.reduce((s, c) => s + c.value, 0))}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,0.55)", flex: 1, textAlign: "right" }}>
                  {currency(donutData.reduce((s, c) => s + c.lastMonth, 0))}
                </Typography>
                <Box sx={{ flex: 1 }} />
              </Stack>
            </Stack>
          )}
        </Box>

        {/* ── Footer ── */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ pt: 1, borderTop: "1px solid #E5E7EB", mt: 1 }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "rgba(0,0,0,0.4)" }}>
            Generated on {today}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 11, color: "rgba(0,0,0,0.4)" }}>
            Page 1 of 1
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
});

export default TeamExpensesReport;
